import { useMutation } from "react-query";

import { APIhandler } from "../../Utils/api";

import { convertObjectToFormData, getPublicIPAddress, injectAttributes, LocalStorage } from "../../Utils/helpers";
import { APIQueryKeys, appUtilityKeys } from "../../Utils/constants";

export const useRequestLogin2FACode = (config = {}) => {
  return useMutation((data) => APIhandler.login.requestLogin2FACode(data), config);
};

export const useLoginWith2FACode = (config = {}) => {
  return useMutation((data) => APIhandler.login.loginWith2FACode(data), config);
};

export const useAuthorizePlaidCoreExchange = (config = {}) => {
  return useMutation((data) => APIhandler.plaidCoreExchange.authorize(data), config);
};

export const useClaimUnclaimedTransactions = (config = {}) => {
  return useMutation(() => APIhandler.unclaimedTransactions.claimUnclaimedTransactions(), config);
};

export const useCreateLinkToken = (config = {}) => {
  return useMutation(() => APIhandler.tokens.createLinkToken(), config);
};

export const useSetAccessToken = (config = {}) => {
  return useMutation((payload) => APIhandler.tokens.setAccessToken(injectAttributes(payload, ["userId"])), config);
};

export const useSaveDefaultConnectedAccount = (config = {}) => {
  return useMutation(
    (payload) => APIhandler.bankConnections.saveDefaultConnectedAcccount(injectAttributes(payload, ["userId"])),
    config
  );
};

export const useResetConnectedAccountSelection = (config = {}) => {
  return useMutation((payload) => {
    return APIhandler.bankConnections.resetConnectedAcccount(payload);
  }, config);
};

export const useAlternativeIdentifyVerify = (config = {}) => {
  return useMutation((data) => APIhandler.alternativeLogin.alternativeIdentifyVerify(data), config);
};

export const useLoginViaAlternativeLogin = (config = {}) => {
  return useMutation((data) => APIhandler.alternativeLogin.loginViaAlternativeLogin(data), config);
};

export const useSaveSpendingFinancialGoals = (config = {}) => {
  return useMutation((data) => APIhandler.onboarding.saveSpendingFinancialGoals(data), config);
};

export const useInitiateVenmoTransfer = (config = {}) => {
  return useMutation((data) => APIhandler.transfers.initiateVenmoTransfer(data), config);
};

export const useInitiatePlaidWithdrawal = (config = {}) => {
  return useMutation((data) => APIhandler.transfers.initiatePlaidWithdrawal(data), config);
};

export const useInitiateACHDebit = (config = {}) => {
  return useMutation((data) => APIhandler.transfers.initiateACHDebit(data), config);
};

export const useOptInCreditReporting = (config = {}) => {
  return useMutation((payload) => APIhandler.creditBuilder.optInCreditReporting(payload), config);
};

export const useOptOutCreditReporting = (config = {}) => {
  return useMutation(() => APIhandler.creditBuilder.optOutCreditReporting(), config);
};

export const useUpdateResident = (config = {}) => {
  return useMutation((data) => APIhandler.resident.updateResident(data), config);
};

export const useSelectPlan = (config = {}) => {
  return useMutation((payload) => APIhandler.resident.selectPlan(payload), config);
};

export const useUpdateAddress = (config = {}) => {
  return useMutation((payload) => APIhandler.resident.updateAddress(payload), config);
};

export const useRequestCheck = (config = {}) => {
  return useMutation(
    ({ mobileUserId, ...payload }) => APIhandler.check.requestCheck(mobileUserId, payload),
    config
  );
};

export const useUpdateEmailAddress = (config = {}) => {
  return useMutation((data) => APIhandler.resident.updateEmailAddress(data), config);
};

export const useUpdatePhoneNumber = (config = {}) => {
  return useMutation((data) => APIhandler.resident.updatePhoneNumber(data), config);
};

export const useUpdateDateOfBirthOrSNN = (config = {}) => {
  return useMutation((data) => APIhandler.resident.updateDateOfBirthOrSNN(data), config);
};

export const useEmailChangeRequestVerification = (config = {}) => {
  return useMutation((payload) => APIhandler.resident.emailChangeRequestVerification(payload), config);
};

export const useEmailChangeVerification = (config = {}) => {
  return useMutation((payload) => APIhandler.resident.emailChangeVerification(payload), config);
};

export const usePhoneNumberChangeRequestVerification = (config = {}) => {
  return useMutation((payload) => APIhandler.resident.phoneNumberChangeRequestVerification(payload), config);
};

export const usePhoneNumberChangeVerification = (config = {}) => {
  return useMutation((payload) => APIhandler.resident.phoneNumberChangeVerification(payload), config);
};

export const useRequestStakeDebitCard = (config = {}) => {
  return useMutation((cardType) => APIhandler.stakeDebit.requestStakeDebitCard(cardType), config);
};

export const useDownloadStatement = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeDebit.getStateDebitStatement(payload), config);
};

export const useLogout = (config = {}) => {
  return useMutation(() => APIhandler.logout.logout(), config);
};

// New Astra mutation endpoints
export const useGetAstraTokenUsingSessionToken = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.getAstraTokenUsingSessionToken(payload), config);
};

export const useGetNewAstraTokens = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.getNewAstraTokens(payload), config);
};

export const useCreateUserIntent = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.createUserIntent(payload), config);
};

export const useCreateTrustedAuthenticationToken = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.createTrustedAuthenticationToken(payload), config);
};

export const useCreateTransferRoutine = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.createTransferRoutine(payload), config);
};

export const useEnableCardToCardPayment = (config = {}) => {
  const payload = { astraToken: LocalStorage.read(appUtilityKeys.astraToken) };
  return useMutation((cardId) => APIhandler.astra.enableCardToCardPayment(cardId, payload), config);
};

export const useDeleteAstraCard = (config = {}) => {
  return useMutation((cardId) => APIhandler.astra.deleteAstraCard(cardId), config);
};

export const useCreateAccountToCardTransferRoutineWithoutAuthToken = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.createAccountToCardTransferRoutineWithoutAuthToken(payload), config);
};

export const useCreateAccountToCardTransferRoutine = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.createAccountToCardTransferRoutine(payload), config);
};

export const useCreateAstraAccount = (config = {}) => {
  return useMutation((payload) => APIhandler.astra.createAstraAccount(payload), config);
};

export const usePayrollLinkToken = (config = {}) => {
  return useMutation(() => APIhandler.payroll.getPayrollLinkToken(), config);
};

export const useUpdatePayrollAccountToken = (config = {}) => {
  return useMutation((accountId) => APIhandler.payroll.getUpdatePayrollAccountToken(accountId), config);
};

export const useDisconnectPayrollAccount = (config = {}) => {
  return useMutation((accountId) => APIhandler.payroll.disconnectPayrollAccount(accountId), config);
};

export const useActivateRecurringDeposit = (config = {}) => {
  return useMutation(
    (payload) => APIhandler.recurringDeposits.activateRecurringDeposit(injectAttributes(payload, ["userId"])),
    config
  );
};

export const useClearRecurringDeposit = (config = {}) => {
  return useMutation(
    () => APIhandler.recurringDeposits.clearRecurringDeposit(injectAttributes({}, ["userId"])),
    config
  );
};

export const useAgreeToS = (config = {}) => {
  return useMutation(
    async () =>
      APIhandler.termsOfService.agreeToS(
        convertObjectToFormData({ current_user_ip_address: await getPublicIPAddress() })
      ),
    config
  );
};

//StakePay 3

export const useStakePay3SetupPayment = (config = {}) => {
  return useMutation((payload) => APIhandler.stakepay3.setupPayment(payload), config);
};

export const useStakePay3CancelPayment = (config = {}) => {
  return useMutation((payload) => APIhandler.stakepay3.cancelPayment(payload), config);
};

export const useStakePay3EditPayment = (config = {}) => {
  return useMutation((payload) => APIhandler.stakepay3.editPayment(payload), config);
};

// PAYMENT METHODS
export const useDeleteFinixCard = (config = {}) => {
  return useMutation((payload) => APIhandler.finix.deleteCard(payload), config);
};

export const useCreateFinixSetupIntent = (config = {}) => {
  return useMutation((payload) => APIhandler.finix.createFinixSetupIntent(payload), config);
};

export const useCreateFinixPaymentInstrument = (config = {}) => {
  return useMutation((payload) => APIhandler.finix.createFinixPaymentInstruments(payload), config);
};

export const useCreateStripeSetupIntent = (config = {}) => {
  return useMutation((payload) => APIhandler.stripe.createStripeSetupIntent(payload), config);
};

//Knot

export const useCreateKnotSession = (config = {}) => {
  return useMutation((payload) => APIhandler.knot.createKnotSession(payload), config);
};

export const useResetInsufficientBalanceForMembershipFeesAlert = (config = {}) => {
  return useMutation(() => APIhandler.stakeDebit.resetInsufficientBalanceForMembershipFeesAlert(), config);
};

export const useResetStakeCheckingClosedForNonComplianceAlert = (config = {}) => {
  return useMutation(() => APIhandler.stakeDebit.resetStakeCheckingClosedForNonComplianceAlert(), config);
};

export const useJoinStakeSaver = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeSaver.joinStakeSaver(payload), config);
};

export const useCancelStakeSaver = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeSaver.cancelStakeSaver(payload), config);
};

export const useSetupStakeSaver = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeSaver.setupStakeSaver(payload), config);
};

export const useUpdateConsumerDealPaymentMethod = (config = {}) => {
  return useMutation((payload) => APIhandler.consumerPaymentMethods.updateConsumerDealPaymentMethod(payload), config);
};

export const useSavePartnerCustomerInformation = (config = {}) => {
  return useMutation((payload) => APIhandler.partnerSignUp.savePartnerCustomerInformation(payload), config);
};

export const useDeleteFidelCard = (config = {}) => {
  return useMutation((cardId) => APIhandler.fidel.deleteFidelCard(cardId), config);
};

export const useDirectSignupCustomer = (config = {}) => {
  return useMutation((payload) => APIhandler.directSignUp.directSignupCustomer(payload), config);
};

export const useUpdateLeaseInformation = (config = {}) => {
  return useMutation(({ data, config }) => APIhandler.directSignUp.updateLeaseInformation(data, config), config);
};

export const useApplyStakeSaverDiscount = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeSaver.applyStakeSaverDiscount(payload), {
    mutationKey: APIQueryKeys.applyingStakeSaverDiscount,
    ...config,
  });
};

export const useAddPayee = (config = {}) => {
  return useMutation((payload) => APIhandler.payee.addPayee(payload), config);
};

export const useDeletePayee = (config = {}) => {
  return useMutation((payload) => APIhandler.payee.deletePayee(payload), config);
};

export const useInitiateOnePayment = (config = {}) => {
  return useMutation((payload) => APIhandler.rentPayment.initiateOneTimePayment(payload), config);
};

export const useCancelOneTimePayment = (config = {}) => {
  return useMutation((payload) => APIhandler.rentPayment.cancelOneTimePayment(payload), config);
};

export const useCreateRecurringRentPayment = (config = {}) => {
  return useMutation((payload) => APIhandler.rentPayment.createRecurringRentPayment(payload), config);
};

export const useUpdateRecurringRentPayment = (config = {}) => {
  return useMutation((payload) => APIhandler.rentPayment.updateRecurringRentPayment(payload), config);
};

export const useCancelRecurringRentPayment = (config = {}) => {
  return useMutation((payload) => APIhandler.rentPayment.cancelRecurringRentPayment(payload), config);
};

export const useSaveInsuranceDetails = (config = {}) => {
  return useMutation((payload) => APIhandler.insurance.saveInsuranceDetails(payload), config);
};

export const useCreateMyUtilitiesOrder = (config = {}) => {
  return useMutation((payload) => APIhandler.myUtilities.createOrder(payload), config);
};

export const useValidateRoutingNumber = (config = {}) => {
  return useMutation((routingNumber) => APIhandler.payee.validateRoutingNumber(routingNumber), config);
};

export const useUpdateProgramPreviewStatus = (config = {}) => {
  return useMutation(() => APIhandler.programs.updateProgramPreviewStatus(), config);
};

export const useApplyStakeChecking = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeDebit.applyStakeChecking(payload), config);
};

export const useMigrateBankAccount = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeDebit.migrateBankAccount(payload), config);
};

export const useAcknowledgeMigration = (config = {}) => {
  return useMutation(() => APIhandler.stakeDebit.acknowledgeMigration(), config);
};

export const useVerifyPersonSynctera = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeDebit.verifyPersonSynctera(payload), config);
};

export const useSaveStakeCheckingProgressState = (config = {}) => {
  return useMutation(
    (payload) => APIhandler.stakeDebit.saveStakeCheckingProgressState(payload),
    config,
  );
};

export const useUpdateCardStatus = (config = {}) => {
  return useMutation(
    ({ status, reason, reasonDetails, cardId }) =>
      status === "SUSPENDED"
        ? APIhandler.stakeDebit.freezeCard({ reason, reasonDetails, cardId })
        : APIhandler.stakeDebit.unfreezeCard({ reason, reasonDetails, cardId }),
    config,
  );
};

export const useTerminateCard = (config = {}) => {
  return useMutation((payload) => APIhandler.stakeDebit.terminateCard(payload), config);
};

export const useCreatePublicGridEmbed = (config = {}) => {
  return useMutation((payload) => APIhandler.publicgrid.createEmbed(payload || {}), config);
};

